// Shared by Cloud Functions and the frontend (imported there as @shared).
// Coaching strategy: what a coach runs, and how computer coaches choose.
// Everything here uses visible ratings only. Hidden system fit (players'
// styles, coaches' profiles) lives in functions src/sim/system.js.
//
// team.strategy: {offense, defense, pace, depth}

export const OFFENSES = [
  {key: "motion", label: "Motion",
    text: "Keep the ball moving. More assists and fewer turnovers; " +
      "best with smart passers."},
  {key: "pickRoll", label: "Pick-and-roll",
    text: "Run it through the point guard and a rolling big. Best with a " +
      "guard who can drive and pass."},
  {key: "post", label: "Post-up",
    text: "Feed the bigs inside. Draws fouls and offensive rebounds, few " +
      "threes. Best with strong forwards and centers."},
  {key: "fiveOut", label: "Five-out",
    text: "Spread the floor and let it fly from three. Great with shooters, " +
      "rough without them."},
  {key: "princeton", label: "Princeton",
    text: "Patient cutting and passing for backdoor layups. Best with smart " +
      "players and big men who can pass."},
  {key: "isolation", label: "Isolation",
    text: "Clear out and let your best scorer go one-on-one. Best with a " +
      "star; few assists."},
];

export const DEFENSES = [
  {key: "man", label: "Man-to-man",
    text: "Everyone guards an assigned player. The steady default."},
  {key: "zone", label: "Zone",
    text: "Protects the paint and cuts fouls, but gives up threes and " +
      "offensive rebounds."},
  {key: "press", label: "Press",
    text: "Full-court pressure: steals and turnovers, but it costs energy " +
      "and fouls, and good passers beat it for easy baskets."},
  {key: "packLine", label: "Pack-line",
    text: "Man-to-man that sags into the lane: stops drives and post-ups, " +
      "gives up threes."},
  {key: "switch", label: "Switch",
    text: "Switch every screen. Needs five players who can guard anyone; " +
      "big men in the post can feast on the mismatches."},
];

export const PACES = [
  {key: "slow", label: "Slow", factor: 0.92,
    text: "Fewer possessions and less running. Helps an underdog keep it " +
      "close."},
  {key: "normal", label: "Normal", factor: 1, text: "The usual tempo."},
  {key: "fast", label: "Fast", factor: 1.08,
    text: "More possessions and more running. Tires players out faster."},
];

export const DEPTHS = [
  {key: "tight", label: "Tight", size: 7,
    text: "The best seven (eight in the pros, where games are longer) play " +
      "almost all the minutes. Needs stamina."},
  {key: "standard", label: "Standard", size: 9,
    text: "A normal nine-player rotation."},
  {key: "deep", label: "Deep", size: 11,
    text: "Lots of rest for everyone; the bench plays more."},
];

export const DEFAULT_STRATEGY = {
  offense: "motion", defense: "man", pace: "normal", depth: "standard",
};

/**
 * A valid strategy: unknown or missing choices fall back to the default.
 * @param {object} s
 * @return {object}
 */
export function cleanStrategy(s) {
  const pick = (list, key, fallback) =>
    (list.some((x) => x.key === key) ? key : fallback);
  return {
    offense: pick(OFFENSES, s?.offense, DEFAULT_STRATEGY.offense),
    defense: pick(DEFENSES, s?.defense, DEFAULT_STRATEGY.defense),
    pace: pick(PACES, s?.pace, DEFAULT_STRATEGY.pace),
    depth: pick(DEPTHS, s?.depth, DEFAULT_STRATEGY.depth),
  };
}

// What a typical lineup scores in each fit below just from how positions
// are built (bigs always block more than guards, ...), so a score of 0
// means "as suited as an average team".
const FIT_OFFSETS = {
  offense: {motion: 0.06, pickRoll: 0.33, post: 0.13, fiveOut: -0.16,
    princeton: -0.16, isolation: 1.2},
  defense: {man: 0.09, zone: 1.05, press: 0.6, packLine: 0.05,
    switch: -0.9},
};

/**
 * How well five players (in slot order PG..C) suit each offense and
 * defense, from visible ratings: about -1 (poor) to 1 (strong) next to the
 * league's average player. The engine uses this for the five on the floor.
 * @param {object[]} five players in slots 0-4
 * @param {number} ratingMean the league's average rating
 * @return {object} {offense: {key: n}, defense: {key: n}}
 */
export function lineupFit(five, ratingMean) {
  const avg = (ps, keys) => ps.reduce((s, p) =>
    s + keys.reduce((n, k) => n + p[k], 0) / keys.length, 0) / ps.length;
  const rel = (x) => (x - ratingMean) / 10;
  const [pg, , , pf, c] = five;
  const scorer = (p) => (p.off_drive + p.off_shooting + p.off_3pt) / 3;
  const guardAnyone = (p) => (p.def_speed + p.def_paint) / 2;
  const raw = {
    offense: {
      motion: rel(avg(five, ["off_passing", "off_smarts"])),
      pickRoll: 0.7 * rel(avg([pg], ["off_drive", "off_passing"])) +
        0.3 * rel(avg([c], ["off_drive"])),
      post: rel(avg([pf, c], ["off_drive", "off_shooting", "off_rebounds"])),
      fiveOut: rel(avg(five, ["off_3pt"])),
      princeton: 0.5 * rel(avg(five, ["off_smarts"])) +
        0.25 * rel(avg([pf, c], ["off_passing"])) +
        0.25 * rel(avg(five, ["off_3pt"])),
      isolation: rel(Math.max(...five.map(scorer))),
    },
    defense: {
      man: rel(avg(five, ["def_speed", "def_smarts"])),
      zone: rel(avg([pf, c], ["def_blocks", "def_paint"])),
      press: rel(avg(five.slice(0, 3), ["def_speed", "def_steals"])),
      packLine: rel(avg(five, ["def_paint", "def_smarts"])),
      switch: rel(Math.min(...five.map(guardAnyone))),
    },
  };
  for (const cat of ["offense", "defense"]) {
    for (const key of Object.keys(raw[cat])) {
      raw[cat][key] -= FIT_OFFSETS[cat][key];
    }
  }
  return raw;
}

/**
 * The likely starting five, one per position (best by overall at each
 * position, then the best left).
 * @param {object[]} list healthy players
 * @return {object[]}
 */
function likelyFive(list) {
  const five = [];
  for (let pos = 1; pos <= 5; pos++) {
    const pick = list.find((p) => p.pos1 === pos && !five.includes(p)) ??
      list.find((p) => !five.includes(p));
    if (pick) five.push(pick);
  }
  return five;
}

/**
 * How well the roster suits each choice, from visible ratings only: a
 * score around 0 for an average roster, higher is a better fit. The
 * Strategy screen shows these, and computer coaches choose by them.
 * @param {object} players {id: player} (healthy players count)
 * @param {object} [ctx] {ratingMean}: the league's average rating
 * @return {object} {offense: {key: score}, defense: {...}, pace: {...},
 *   depth: {...}}
 */
export function rosterFit(players, {ratingMean = 50} = {}) {
  const list = Object.values(players)
      .filter((p) => !(p.injury?.gamesOut > 0))
      .sort((a, b) => b.overall - a.overall);
  const five = likelyFive(list);
  const fit = five.length === 5 ? lineupFit(five, ratingMean) :
    {offense: Object.fromEntries(OFFENSES.map((o) => [o.key, 0])),
      defense: Object.fromEntries(DEFENSES.map((d) => [d.key, 0]))};
  const avgOf = (ps, key) => ps.length ?
    ps.reduce((s, p) => s + p[key], 0) / ps.length : 0;
  const stamina = avgOf(list.slice(0, 7), "stamina");
  // How much better the top seven are than the next four.
  const gap = avgOf(list.slice(0, 7), "overall") -
    avgOf(list.slice(7, 11), "overall");
  // The "talent" part every option shares: what makes one option better
  // than another for this roster is the rest.
  const shared = (cat) => Object.values(fit[cat])
      .reduce((a, b) => a + b, 0) / Object.keys(fit[cat]).length;
  const relative = (cat) => Object.fromEntries(Object.entries(fit[cat])
      .map(([k, v]) => [k, Math.round((v - shared(cat)) * 100) / 100]));
  return {
    offense: relative("offense"),
    defense: relative("defense"),
    // A typical top seven has stamina about 7 and is about 9 rating points
    // better than the next four.
    pace: {
      slow: -(stamina - 7) / 1.5,
      normal: 0,
      fast: (stamina - 7) / 1.5,
    },
    depth: {
      tight: (gap - 9) / 8 + (stamina - 7) / 1.5,
      standard: 0,
      deep: -(gap - 9) / 8 - (stamina - 7) / 3,
    },
  };
}

/**
 * What a computer coach runs: the best visible fit in each category
 * (never hidden system fit, so its choices can't hint at it).
 * @param {object} players
 * @param {object} [ctx] {ratingMean}
 * @return {object} strategy
 */
export function autoStrategy(players, ctx) {
  const fit = rosterFit(players, ctx);
  const best = (scores) => Object.entries(scores)
      .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))[0][0];
  // A computer coach changes things only for a clear reason.
  const pick = (scores, fallback, margin) => {
    const b = best(scores);
    return scores[b] - scores[fallback] > margin ? b : fallback;
  };
  return {
    offense: best(fit.offense),
    defense: pick(fit.defense, "man", 0.15),
    pace: pick(fit.pace, "normal", 0.3),
    depth: pick(fit.depth, "standard", 0.4),
  };
}

/**
 * @param {Array} list OFFENSES, DEFENSES, PACES or DEPTHS
 * @param {string} key
 * @return {string} its label
 */
export function strategyLabel(list, key) {
  return list.find((x) => x.key === key)?.label ?? key;
}
